import { css } from '@emotion/react'
import { Alert, Grid, SortDirection, Spinner, TableFooter, Text, Theme, useTheme, VFlow } from 'bold-ui'
import { confirm } from 'components/modals/confirm'
import { usePagedTableProps } from 'components/table/usePagedTableProps'
import { useFlags } from 'config/useFlagsContext'
import { Dispatch, SetStateAction, useMemo } from 'react'
import { Accordion } from 'react-accessible-accordion'

import { useHistoricoPrint } from '../../hooks/useHistoricoPrint'
import {
  CidadaoHistorico,
  HistoricoAtendimentoFilterFormModel,
  HistoricoAtendimentoListagemTableModel,
  HistoricoAtendimentoModel,
} from '../../model/model-historico'
import HistoricoAtendimentoTableHeader from './header/HistoricoAtendimentoTableHeader'
import HistoricoAtendimentoTableItem from './item/HistoricoAtendimentoTableItem'

export interface HistoricoAtendimentoTableProps {
  cidadao: CidadaoHistorico
  isLoading: boolean
  dataTable: HistoricoAtendimentoListagemTableModel
  sort: SortDirection
  countAtendimentosEnabledToPrint: number
  historicoPrintProps: ReturnType<typeof useHistoricoPrint>
  tableProperties: ReturnType<typeof usePagedTableProps>
  onSortChange(sort: SortDirection): void
  onChangeFilter: Dispatch<SetStateAction<HistoricoAtendimentoFilterFormModel>>
  refetchOnListagem(): void
}

const HISTORICO_PAGE_SIZES = [10, 30, 50, 100]

export default function HistoricoAtendimentoTable(props: HistoricoAtendimentoTableProps) {
  const {
    dataTable,
    isLoading,
    sort,
    onSortChange,
    cidadao,
    refetchOnListagem,
    tableProperties,
    countAtendimentosEnabledToPrint,
    historicoPrintProps,
  } = props
  const theme = useTheme()
  const {
    PILOTO_RETIFICACAO_ATENDIMENTO_PROCEDIMENTOS_ENABLED,
    RETIFICACAO_ATENDIMENTO_INDIVIDUAL_ENABLED,
  } = useFlags()

  const content = dataTable?.content
  const isHistoricoEmpty = content?.length === 0
  const classes = useMemo(() => styles(theme), [theme])

  const handleSortChange = (sort: SortDirection) => onSortChange(sort)

  const {
    print: printSelectedItems,
    toggleSelectItem: handleSelectItem,
    isItemSelected: isAtendimentoSelected,
    isAllPageItemsSelected,
    countSelectedInPage,
    toggleSelectPage: handleSelectPage,
    reset: clearSelections,
  } = historicoPrintProps

  const handlePrintSelectedItems = confirm({
    title: 'Deseja continuar a impressão?',
    subtitle: 'Serão impressos apenas os atendimentos selecionados.',
    body:
      PILOTO_RETIFICACAO_ATENDIMENTO_PROCEDIMENTOS_ENABLED || RETIFICACAO_ATENDIMENTO_INDIVIDUAL_ENABLED ? (
        <Alert type='info' styles={classes.alertPrintSelectedItems}>
          Somente os registros ativos dos atendimentos selecionados serão impressos. Registros inativos precisam ser
          impressos individualmente.
        </Alert>
      ) : undefined,
    onConfirm: printSelectedItems,
    confirmLabel: 'Sim, continuar',
    cancelLabel: 'Não, voltar',
  })

  const { page, onSizeChange, ...restTableProps } = tableProperties

  const handlePrintPage = (selected: boolean) => handleSelectPage(selected, content)

  const handleSizeChange = (size: number) => {
    clearSelections()
    return onSizeChange(size)
  }

  return (
    <VFlow style={classes.tableBox}>
      <HistoricoAtendimentoTableHeader
        sort={sort}
        onSortChange={handleSortChange}
        atendimentosEnabledToPrintCount={countAtendimentosEnabledToPrint}
        selectedCount={countSelectedInPage}
        selectPageToPrint={handlePrintPage}
        isAllPageItemsSelected={isAllPageItemsSelected}
        printHistorico={handlePrintSelectedItems}
      />
      <VFlow vSpacing={0}>
        {isLoading && (
          <div css={classes.loadingContainer}>
            <Spinner />
            Carregando resultados
          </div>
        )}
        {!isLoading && isHistoricoEmpty ? (
          <div css={classes.noResultContainer}>
            <Text fontStyle='italic'>A busca não encontrou resultados</Text>
          </div>
        ) : (
          <Accordion allowZeroExpanded={true} allowMultipleExpanded={true}>
            <Grid style={classes.accordionWrapper}>
              {content?.map((historicoAtendimento: HistoricoAtendimentoModel) => (
                <HistoricoAtendimentoTableItem
                  key={
                    historicoAtendimento.idAtendRecente ??
                    historicoAtendimento.codigoUnicoRegistro ??
                    historicoAtendimento.idAtividadeColetiva ??
                    (historicoAtendimento.cuidadoCompartilhadoEvolucao.id &&
                      `cuidadoCompartilhadoEvolucao-${historicoAtendimento.cuidadoCompartilhadoEvolucao.id}`)
                  }
                  historicoAtendimento={historicoAtendimento}
                  cidadao={cidadao}
                  refetchOnListagem={refetchOnListagem}
                  checked={isAtendimentoSelected(historicoAtendimento)}
                  handleSelectToPrint={handleSelectItem}
                  historicoPrintProps={historicoPrintProps}
                />
              ))}
            </Grid>
          </Accordion>
        )}
      </VFlow>
      <div css={classes.footerWrapper}>
        <TableFooter
          {...restTableProps}
          page={page}
          onSizeChange={handleSizeChange}
          sizeOptions={HISTORICO_PAGE_SIZES}
          pageSize={dataTable?.pageInfo.size}
          style={classes.tableFooter}
        />
      </div>
    </VFlow>
  )
}

const styles = (theme: Theme) => {
  return {
    tableBox: css`
      border: 1px solid ${theme.pallete.divider};
      border-radius: ${theme.radius.modal};
    `,
    accordionWrapper: css`
      padding: 0.5rem 1rem;
    `,
    footerWrapper: css`
      border-top: 1px solid ${theme.pallete.divider};
    `,
    tableFooter: css`
      border: none;
    `,
    loadingContainer: css`
      color: ${theme.pallete.primary.main};
      font-weight: bold;
      display: flex;
      align-items: center;
      justify-content: center;
      & > *:first-of-type {
        margin-right: 0.5rem;
      }
    `,
    noResultContainer: css`
      text-align: center;
      & > span {
        color: ${theme.pallete.text.secondary};
      }
    `,
    alertPrintSelectedItems: {
      container: css`
        padding: 0.5rem;
      `,
      wrapper: css`
        padding: 0;
      `,
    },
  }
}
